import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FaPlus, FaMusic, FaCog, FaClock, FaLayerGroup, FaUsers, FaStickyNote, FaFolder, FaExchangeAlt, FaThLarge, FaChevronDown, FaLock } from 'react-icons/fa';
import './Navigation.css';
import { getDirtyFlag, setDirtyFlag } from './safeNavigate';
import { useAuth } from './AuthProvider';
import { getUnreadNotificationCount } from './utils/socialFirestore';
import { useUserPrefs } from './UserPrefsProvider';
import { ACTIVE_SESSION_STORAGE_KEY } from './ActiveSessionContext';
import { useActiveSession } from './ActiveSessionContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import ActionConfirmModal from './components/ActionConfirmModal';
import { useOnboarding } from './onboarding';


const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { labels, prefs, savePrefs } = useUserPrefs();
  const { activeSession, setActiveSession } = useActiveSession();
  const [unreadCount, setUnreadCount] = useState(0);
  const [modeMenuOpen, setModeMenuOpen] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState(null);
  const modeConfirmResolverRef = useRef(null);
  const [modeConfirm, setModeConfirm] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    danger: false,
  });
  const appMode = prefs?.appMode || 'tunes';
  const searchIcon = appMode === 'notes' ? <FaStickyNote /> : appMode === 'all' ? <FaThLarge /> : <FaMusic />;
  const { isSimpleMode, unlockFullMode } = useOnboarding();
  const [lockPopup, setLockPopup] = useState(null); // { title, message } | null
  const setsIcon = appMode === 'notes' ? <FaFolder /> : <FaLayerGroup />;
  const compactLabel = (label) => (typeof label === 'string' ? label.replace(/^AllMy\s*/i, '') : label);

  const navItems = [
    { path: '/add', label: 'Add', icon: <FaPlus /> },
    { path: '/search', label: compactLabel(labels.navSearch), icon: searchIcon },
    { path: '/sets', label: compactLabel(labels.navSets), icon: setsIcon },
    { path: '/sessions', label: compactLabel(labels.navSessions), icon: <FaClock /> },
    { path: '/social', label: 'Social', icon: <FaUsers /> },
    { path: '/settings', label: 'Settings', icon: <FaCog /> }
  ];

  useEffect(() => {
    if (!user) { setUnreadCount(0); return; }
    const refresh = () => {
      getUnreadNotificationCount(user.uid)
        .then(count => setUnreadCount(count))
        .catch(() => {});
    };
    refresh();
    window.addEventListener('notificationUpdate', refresh);
    return () => window.removeEventListener('notificationUpdate', refresh);
  }, [user, location.pathname]);

  useEffect(() => {
    const handleWindowClick = () => setModeMenuOpen(false);
    window.addEventListener('click', handleWindowClick);
    return () => window.removeEventListener('click', handleWindowClick);
  }, []);

  const requestModeConfirm = ({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', danger = false }) => {
    return new Promise((resolve) => {
      modeConfirmResolverRef.current = resolve;
      setModeConfirm({
        open: true,
        title,
        message,
        confirmLabel,
        cancelLabel,
        danger,
      });
    });
  };

  const handleModeConfirmClose = (confirmed) => {
    const resolver = modeConfirmResolverRef.current;
    modeConfirmResolverRef.current = null;
    setModeConfirm((prev) => ({ ...prev, open: false }));
    if (resolver) resolver(confirmed);
  };

  const handleModeChange = async (newMode) => {
    setModeMenuOpen(false);
    if (newMode === appMode || !user) return;
    const isTuneDetailRoute = /^\/tune\/[^/]+/.test(location.pathname);
    const isEditTuneRoute = /^\/edit\/[^/]+/.test(location.pathname);

    if (isEditTuneRoute && getDirtyFlag()) {
      const ok = await requestModeConfirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes on this edit page. Switch mode and discard those changes?',
        confirmLabel: 'Switch Mode',
        cancelLabel: 'Stay',
        danger: true,
      });
      if (!ok) return;
      setDirtyFlag(false);
    }

    if (isTuneDetailRoute || isEditTuneRoute) {
      const ok = await requestModeConfirm({
        title: 'Switch Mode',
        message: 'Switching mode from this page will return you to your library list. Continue?',
        confirmLabel: 'Continue',
        cancelLabel: 'Cancel',
      });
      if (!ok) return;
    }

    if (activeSession) {
      const ok = await requestModeConfirm({
        title: 'Active Session',
        message: 'You have an active session. End it and switch modes?',
        confirmLabel: 'End Session',
        cancelLabel: 'Cancel',
        danger: true,
      });
      if (!ok) return;
      try {
        const sessionRef = doc(db, 'users', user.uid, 'sessions', activeSession.id);
        await updateDoc(sessionRef, { endedAt: new Date().toISOString() });
        setActiveSession(null);
        localStorage.removeItem(ACTIVE_SESSION_STORAGE_KEY);
      } catch (err) {
        console.error('Failed to end session before switching mode:', err);
      }
    }

    await savePrefs({ ...prefs, appMode: newMode });
    document.body.setAttribute('data-app-mode', newMode);
    localStorage.removeItem('activeSearchFilters');
    localStorage.removeItem('setsFilters');

    if (isTuneDetailRoute || isEditTuneRoute) {
      navigate('/search', { replace: true });
    }
  };

  return (
    <nav className="nav-container">
      {/*
        Nav mode switcher intentionally disabled.
        Kept here commented so it can be restored quickly if needed.
      <div
        className={`nav-item nav-mode-item ${modeMenuOpen ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setModeMenuOpen((open) => !open);
        }}
      >
        <div className="nav-icon">
          <FaExchangeAlt />
        </div>
        <div className="nav-label nav-mode-label">
          <span>Mode</span>
          <FaChevronDown className={`nav-mode-caret ${modeMenuOpen ? 'open' : ''}`} />
        </div>
        {modeMenuOpen && (
          <div className="mode-menu" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="mode-menu-item" onClick={() => handleModeChange('tunes')}>
              Tunes{appMode === 'tunes' ? ' ✓' : ''}
            </button>
            <button type="button" className="mode-menu-item" onClick={() => handleModeChange('notes')}>
              Notes{appMode === 'notes' ? ' ✓' : ''}
            </button>
            <button type="button" className="mode-menu-item" onClick={() => handleModeChange('all')}>
              All{appMode === 'all' ? ' ✓' : ''}
            </button>
          </div>
        )}
      </div>
      */}
      {navItems.map(item => {
        const locked = isSimpleMode && (item.path === '/sessions' || item.path === '/social');
        const lockDescriptions = {
          '/sessions': { title: 'Sessions', message: 'Sessions let you log your playing history — what you played, when, and for how long. Full Mode also unlocks Social (follow musicians, share tunes), advanced search & filters, and program templates.' },
          '/social': { title: 'Social', message: 'Social lets you follow other musicians, share tunes and sets, and receive material from people you follow. Full Mode also unlocks Sessions (playing history), advanced search & filters, and program templates.' },
        };

        if (locked) {
          return (
            <div
              key={item.path}
              className="nav-item"
              style={{ opacity: 0.42, cursor: 'pointer', position: 'relative' }}
              onClick={() => setLockPopup(lockDescriptions[item.path])}
            >
              <div className="nav-icon" style={{ position: 'relative' }}>
                {item.icon}
                <FaLock style={{ position: 'absolute', bottom: '-2px', right: '-6px', fontSize: '9px', opacity: 0.8 }} />
              </div>
              <div className="nav-label">{item.label}</div>
            </div>
          );
        }

        return (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={(e) => {
              e.preventDefault();
              if (getDirtyFlag()) {
                setPendingNavPath(item.path);
                return;
              }
              navigate(item.path);
            }}
            className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
          >
            <div className="nav-icon" style={{ position: 'relative' }}>
              {item.icon}
              {item.path === '/social' && unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-8px',
                  backgroundColor: '#e44',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1
                }}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <div className="nav-label">{item.label}</div>
          </NavLink>
        );
      })}

      <ActionConfirmModal
        open={Boolean(lockPopup)}
        title={lockPopup?.title || ''}
        message={lockPopup?.message || ''}
        confirmLabel="Enable Full Mode"
        cancelLabel="Not yet"
        onConfirm={() => { setLockPopup(null); unlockFullMode(); }}
        onCancel={() => setLockPopup(null)}
      />

      <ActionConfirmModal
        open={Boolean(pendingNavPath)}
        title="Unsaved Changes"
        message="You have unsaved changes. Leave this page and discard them?"
        confirmLabel="Leave"
        cancelLabel="Stay"
        danger
        onConfirm={() => {
          if (!pendingNavPath) return;
          const path = pendingNavPath;
          setPendingNavPath(null);
          setDirtyFlag(false);
          navigate(path);
        }}
        onCancel={() => setPendingNavPath(null)}
      />

      <ActionConfirmModal
        open={modeConfirm.open}
        title={modeConfirm.title}
        message={modeConfirm.message}
        confirmLabel={modeConfirm.confirmLabel}
        cancelLabel={modeConfirm.cancelLabel}
        danger={modeConfirm.danger}
        onConfirm={() => handleModeConfirmClose(true)}
        onCancel={() => handleModeConfirmClose(false)}
      />
    </nav>
  );
};

export default Navigation;
